function signedField(rawValue, signValue, decimalPlaces) {
  const raw = String(rawValue || "").trim();
  if (!raw || !/^\d+$/.test(raw)) return null;
  const sign = String(signValue || "").trim() === "-" ? -1 : 1;
  return sign * Number(raw) / (10 ** decimalPlaces);
}

function compactWhitespace(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function parseCcyymmdd(rawValue) {
  const raw = String(rawValue || "").trim();
  if (!/^\d{8}$/.test(raw) || raw === "00000000") return null;
  const iso = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.valueOf()) || date.toISOString().slice(0, 10) !== iso ? null : iso;
}

export function parseBnyPershingGcus(contents) {
  const lines = String(contents || "").split(/\r?\n/).filter((line) => line.length);
  const header = lines.find((line) => line.startsWith("BOF")) || "";
  const trailer = lines.find((line) => line.startsWith("EOF")) || "";
  const dateMatch = header.match(/DATA OF\s+(\d{2})\/(\d{2})\/(\d{4})/);
  const fileDate = dateMatch ? `${dateMatch[3]}-${dateMatch[1]}-${dateMatch[2]}` : null;
  const fileMode = /\bREFRESHED\b/.test(header) ? "REFRESHED" : /\bUPDATED\b/.test(header) ? "UPDATED" : null;
  const blocks = new Map();
  const errors = [];

  lines.forEach((line, index) => {
    if (line.startsWith("BOF") || line.startsWith("EOF")) return;
    if (line.length !== 750) {
      errors.push({ line_number: index + 1, code: "invalid_record_length", actual_length: line.length });
      return;
    }
    if (!/^GC[AB]/.test(line)) {
      errors.push({ line_number: index + 1, code: "unexpected_record_type", record_type: line.slice(0, 3) });
      return;
    }
    const type = line[2];
    const key = `${line.slice(3, 11)}|${line.slice(11, 21)}|${line.slice(21, 30)}`;
    const block = blocks.get(key) || { records: {}, lineNumbers: {} };
    if (block.records[type]) errors.push({ line_number: index + 1, code: "duplicate_record_type", key, record_type: type });
    block.records[type] = line;
    block.lineNumbers[type] = index + 1;
    blocks.set(key, block);
  });

  const rows = [];
  const rejected = [];
  for (const [key, block] of blocks) {
    const a = block.records.A;
    const b = block.records.B;
    const reasons = [];
    if (!a) reasons.push("missing_record_a");
    const sequenceNumber = a?.slice(3, 11) || key.split("|")[0];
    const accountNumber = a?.slice(11, 21).trim() || key.split("|")[1];
    const accountKey = accountNumber.slice(0, 9);
    const accountType = accountNumber.slice(9, 10) || null;
    const cusip = a?.slice(21, 30).trim().toUpperCase() || key.split("|")[2];
    if (!/^[A-Z0-9]{10}$/.test(accountNumber)) reasons.push("invalid_account_number");
    if (!/^[A-Z0-9]{9}$/.test(cusip)) reasons.push("invalid_cusip");

    const tradeQuantity = a ? signedField(a.slice(73, 91), a.slice(91, 92), 5) : null;
    const settledQuantity = a ? signedField(a.slice(92, 110), a.slice(110, 111), 5) : null;
    const pendingSplitQuantity = a ? signedField(a.slice(282, 300), a.slice(300, 301), 5) : null;
    const tradeLiquidatingValue = a ? signedField(a.slice(664, 682), a.slice(682, 683), 3) : null;
    const settledLiquidatingValue = a ? signedField(a.slice(713, 731), a.slice(731, 732), 3) : null;
    const pendingSplitLiquidatingValue = b ? signedField(b.slice(453, 471), b.slice(471, 472), 3) : 0;
    if (tradeQuantity == null) reasons.push("invalid_trade_date_quantity");
    if (settledQuantity == null) reasons.push("invalid_settled_quantity");

    const parsed = {
      sequence_number: sequenceNumber,
      account_number: accountNumber,
      account_key: accountKey,
      account_type: accountType,
      cusip,
      currency_security_indicator: a?.slice(53, 54).trim() || null,
      trade_quantity_date: a ? parseCcyymmdd(a.slice(57, 65)) : null,
      settled_quantity_date: a ? parseCcyymmdd(a.slice(65, 73)) : null,
      trade_date_quantity: tradeQuantity,
      settled_quantity: settledQuantity,
      pending_split_quantity: pendingSplitQuantity || 0,
      authoritative_quantity: tradeQuantity == null ? null : tradeQuantity + (pendingSplitQuantity || 0),
      trade_liquidating_value: tradeLiquidatingValue == null ? null
        : tradeLiquidatingValue + (pendingSplitLiquidatingValue || 0),
      settled_liquidating_value: settledLiquidatingValue == null ? null
        : settledLiquidatingValue + (pendingSplitLiquidatingValue || 0),
      accrued_interest: b ? signedField(b.slice(416, 434), b.slice(434, 435), 3) : null,
      coupon_rate: b ? signedField(b.slice(435, 453), "+", 9) : null,
      account_short_name: a?.slice(513, 523).trim() || null,
      position_currency: a?.slice(661, 664).trim() || null,
      security_description: a ? compactWhitespace(a.slice(537, 657)) : null,
      source_line_a: block.lineNumbers.A || null,
      source_line_b: block.lineNumbers.B || null,
    };
    if (reasons.length) rejected.push({ ...parsed, reasons });
    else rows.push(parsed);
  }

  const trailerCountMatch = trailer.match(/TOTAL DETAIL RECORDS:\s*(\d+)/);
  const declaredDetailCount = trailerCountMatch ? Number(trailerCountMatch[1]) : null;
  const actualDetailCount = lines.filter((line) => !line.startsWith("BOF") && !line.startsWith("EOF")).length;
  if (!header) errors.push({ code: "missing_header" });
  if (!trailer) errors.push({ code: "missing_trailer" });
  if (fileMode !== "REFRESHED") errors.push({ code: "gcus_not_refreshed", actual: fileMode });
  if (declaredDetailCount != null && declaredDetailCount !== actualDetailCount) {
    errors.push({ code: "detail_count_mismatch", declared: declaredDetailCount, actual: actualDetailCount });
  }

  return {
    source_system: "bny_pershing", file_type: "GCUS", file_date: fileDate,
    file_mode: fileMode, declared_detail_count: declaredDetailCount,
    actual_detail_count: actualDetailCount, holding_count: blocks.size,
    rows, rejected, errors,
  };
}
